import React from "react";
import DOMPurify from "dompurify";

/**
 * SafeHtml Component
 * Safely renders HTML content using DOMPurify for sanitization
 * This component ensures all HTML is sanitized before rendering to prevent XSS attacks
 * 
 * @param {string} html - The HTML string to render (will be sanitized)
 * @param {string} className - Optional CSS class name
 * @param {object} style - Optional inline styles
 * @param {string} tag - HTML tag to use (default: 'div')
 * @param {object} props - Additional props to pass to the element
 */
const SafeHtml = ({ 
    html = '', 
    className = 'event-title-cal', 
    style = {}, 
    tag = 'div',
    ...props 
}) => {
    // Always sanitize HTML content, even if empty
    const sanitizedHtml = DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'div', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['class', 'href', 'rel'],
        ALLOW_DATA_ATTR: false,
        FORCE_BODY: false,
    });
    // Enforce noopener on all anchor tags post-sanitize
    const withSafeLinks = sanitizedHtml.replace(/<a\s/gi, '<a rel="noopener noreferrer" ');

    // Use React.createElement to dynamically create the element
    return React.createElement(tag, {
        className,
        style,
        dangerouslySetInnerHTML: { __html: withSafeLinks },
        ...props,
    });
};

/**
 * SafeHtmlText Component
 * Renders HTML as plain text (strips all HTML tags)
 * Use this when HTML formatting is not needed
 * 
 * @param {string} html - The HTML string to convert to plain text
 * @param {string} className - Optional CSS class name
 * @param {object} style - Optional inline styles
 * @param {string} tag - HTML tag to use (default: 'div')
 */
export const SafeHtmlText = ({ 
    html = '', 
    className = '', 
    style = {}, 
    tag = 'div',
    ...props 
}) => {
    // Convert HTML to plain text by creating a temporary element
    const getPlainText = (htmlString) => {
        if (!htmlString) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = htmlString;
        return tmp.textContent || tmp.innerText || '';
    };

    const plainText = getPlainText(html);

    return React.createElement(tag, {
        className,
        style,
        ...props,
    }, plainText);
};

export default SafeHtml;

